import DataLoader from 'dataloader'
import { collection } from '@/infra/collection'
import { LegendVersionId, PublishedLegend } from './PublishedLegend'
import { Id } from '@/infra/id'
import { mapPublishedLegendResourceToProps } from './published-legend-mappers'
import type { LegendsApi } from '../api/legend-api'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { NotFound } from 'fejl'

/**
 * The store for {@link PublishedLegend}.
 */
export class PublishedLegendStore {
  private readonly _loader = new DataLoader(this._fetchManyCore.bind(this))

  /**
   * The collection of {@link PublishedLegend}.
   */
  readonly publishedLegends = collection({
    create: () => new PublishedLegend(),
    getDataId: (attrs) =>
      LegendVersionId.asString(
        LegendVersionId(attrs.id || '?', attrs.version ?? -1),
      ),
    getModelId: (model) => LegendVersionId.asString(model.versionId),
  })

  constructor(private readonly _api: LegendsApi) {}

  /**
   * Lists {@link PublishedLegend} for a given workspace.
   */
  @memoizeAsync()
  async listPublishedLegends(workspaceId: Id, signal?: AbortSignal) {
    const resources = await this._api.publishedLegends.list(
      {
        workspace_id: Id.assert(workspaceId),
        // We need all the legends.
        limit: 999999,
      },
      signal,
    )

    return this.publishedLegends.set(
      resources.data.map(mapPublishedLegendResourceToProps),
    )
  }

  /**
   * Fetches the latest published Legend version.
   *
   * @param id
   */
  async fetchLatestPublishedLegendVersion(
    id: Id,
  ): Promise<PublishedLegend | null> {
    id = Id.assert(id)
    return await this._loader.load(id)
  }

  /**
   * Fetches multiple published legends at once.
   *
   * @param ids
   * @private
   */
  private async _fetchManyCore(ids: readonly string[]) {
    // NOTE: The get-many API requires admin permission, so can't use it
    // right now.
    const resources = await Promise.all(
      ids.map((id) =>
        this._api.publishedLegends.get(id).catch(NotFound.ignore(null)),
      ),
    )

    return this.publishedLegends.set(
      resources.map((r) => (r ? mapPublishedLegendResourceToProps(r) : null)),
    )
  }
}
